import { Tooltip } from "@george-labs.com/design-system"
import { useCallback, useEffect, useRef, useState } from "react"

interface IEllipsisProps {
  text: string
  lines?: number
  lineHeight?: number
  style?: React.CSSProperties
}

const Ellipsis: React.FC<IEllipsisProps> = ({
  text,
  lines = 3,
  lineHeight = 1.5,
  style,
}) => {
  const ref = useRef<HTMLDivElement | null>(null)
  const [displayText, setDisplayText] = useState(text)

  const updateText = useCallback(() => {
    if (ref.current) {
      const fontSize = Number.parseFloat(getComputedStyle(ref.current).fontSize)
      const lineHeightPx = fontSize * lineHeight
      const maxHeightPx = lines * lineHeightPx

      ref.current.innerText = text

      if (ref.current.scrollHeight > maxHeightPx) {
        let newText = text
        while (ref.current.scrollHeight > maxHeightPx && newText.length > 0) {
          newText = newText.slice(0, -1)
          ref.current.innerText = newText + "..."
        }
        setDisplayText(newText + "...")
      } else {
        setDisplayText(text)
      }
    }
  }, [lineHeight, lines, text])

  useEffect(() => {
    updateText()

    const handleResize = () => {
      updateText()
    }

    window.addEventListener("resize", handleResize)

    return () => {
      window.removeEventListener("resize", handleResize)
    }
  }, [lineHeight, lines, text, updateText])

  const content = (
    <span
      ref={ref}
      style={{
        display: "block",
        overflow: "hidden",
        lineHeight: `${lineHeight}em`,
        maxHeight: `${lines * lineHeight}em`,
        ...style,
      }}
    >
      {displayText}
    </span>
  )

  if (text.length !== displayText.length) {
    return (
      <Tooltip
        content={text}
        enabled={false}
        usePortal={true}
        showOnHover={true}
      >
        {content}
      </Tooltip>
    )
  }

  return content
}

export default Ellipsis
